import React, { useContext, useCallback, Fragment, useState } from 'react';
import Store from '../Store/Store';
import './PerDiem.css';
import Comment from '../Comment/Comment';
import EmployeeInfo from '../EmployeeInfo/EmployeeInfo';
import CbmContext from '../../Context/Cbm/cbmContext';
import { useFormSetup } from '../../hooks/useFormSetup';
import ValidationErrors from '../Utils/ValidationErrors';
import { findStoreById } from '../Utils/helperFuncs';

const PerDiem = ({ setPayLoad, setFormError, errMsg }) => {
  const cbmContext = useContext(CbmContext);
  const { stores, user, submittedData, errorCode } = cbmContext;

  const [perDiemData, setPerDiemData] = useState({
    employee: {
      employeeNumber: '',
      employeeFirst: '',
      employeeMiddle: '',
      employeeLast: '',
      employeeSecondLast: '',
    },
    firstHotelNight: '',
    lastHotelNight: '',
    arrivalDate: '',
    departureDate: '',
    mileageDates: [
      {
        date: '',
        location1: null,
        location2: null,
        rt_ow: null,
      },
    ],
    store: null,
    reasonPayMissed: '',
    portalUser: user,
  });

  // Use unified form setup hook
  const {
    validationErrors,
    getFieldError,
    getArrayFieldError,
    hasArrayFieldError,
    clearArrayItemErrors,
  } = useFormSetup({
    formName: 'perDiem',
    formData: perDiemData,
    setFormData: setPerDiemData,
    setPayLoad,
    setFormError,
    submittedData,
    errorCode,
  });

  const onHandleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setPerDiemData((prev) => ({ ...prev, [name]: value }));
    },
    [setPerDiemData]
  );

  const addMileageEntry = useCallback(() => {
    const mileageEntryTemplate = {
      date: '',
      location1: '',
      location2: '',
      rt_ow: '',
    };
    setPerDiemData((prev) => ({
      ...prev,
      mileageDates: [...prev.mileageDates, mileageEntryTemplate],
    }));
  }, [setPerDiemData]);

  const deleteMileageEntry = useCallback(
    (index) => {
      clearArrayItemErrors('mileageDates', index);
      setPerDiemData((prev) => ({
        ...prev,
        mileageDates: prev.mileageDates.filter((_, i) => i !== index),
      }));
    },
    [setPerDiemData, clearArrayItemErrors]
  );

  const updateMileages = useCallback(
    (e, index) => {
      const { name, value, id } = e.target;
      setPerDiemData((prev) => {
        const mileageTemp = [...prev.mileageDates];
        if (name === 'store' || name === 'location1' || name === 'location2') {
          const foundStore = findStoreById(stores, parseInt(value));
          mileageTemp[index][name] = foundStore;
        } else if (id === 'rt' || id === 'ow') {
          const radioOption = name.split('_').slice(0, 2).join('_');
          mileageTemp[index][radioOption] = value;
        } else {
          mileageTemp[index][name] = value;
        }
        return { ...prev, mileageDates: mileageTemp };
      });
    },
    [stores, setPerDiemData]
  );

  const changeStoreInfo = useCallback(
    (e) => {
      const { value } = e.target;
      const foundStore = findStoreById(stores, parseInt(value));
      setPerDiemData((prev) => ({ ...prev, store: foundStore }));
    },
    [stores, setPerDiemData]
  );

  const changeEmpInfo = useCallback(
    (e) => {
      const { name, value } = e.target;
      setPerDiemData((prev) => ({
        ...prev,
        employee: { ...prev.employee, [name]: value },
      }));
    },
    [setPerDiemData]
  );

  const handleComments = useCallback(
    (e) => {
      const { value } = e.target;
      setPerDiemData((prev) => ({ ...prev, reasonPayMissed: value }));
    },
    [setPerDiemData]
  );
  return (
    <Fragment>
      <div className="heading">
        <h1>PerDiem</h1>
      </div>
      {errMsg && (
        <div className="error-message-container">
          <p className="error-message">{errMsg}</p>
          <ValidationErrors errors={validationErrors} />
        </div>
      )}
      <fieldset>
        <legend>Employee Info</legend>
        <EmployeeInfo
          payload={perDiemData}
          changeEmpInfo={changeEmpInfo}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>
      <fieldset className="location-info">
        <legend>Store Info</legend>
        <Store
          storeList={stores}
          header="Store:"
          stoerValue={perDiemData.store?.id}
          elementName="store"
          changeStoreInfo={changeStoreInfo}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>
      <fieldset className="perDiemHotel">
        <legend>Hotel Info</legend>
        <div className="perDiemHotelInfo">
          <div
            className={
              errMsg && getFieldError('firstHotelNight')
                ? 'firstHotelNight error'
                : 'firstHotelNight'
            }
          >
            <label className="required">First hotel night needed: </label>
            <input
              type="date"
              name="firstHotelNight"
              value={perDiemData.firstHotelNight || ''}
              required
              onChange={(e) => onHandleChange(e)}
            />
            {errMsg && getFieldError('firstHotelNight') && (
              <span className="error-text">
                {getFieldError('firstHotelNight')}
              </span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('lastHotelNight')
                ? 'lastHotelNight error'
                : 'lastHotelNight'
            }
          >
            <label className="required">Last hotel night needed: </label>
            <input
              type="date"
              name="lastHotelNight"
              value={perDiemData.lastHotelNight || ''}
              required
              onChange={(e) => onHandleChange(e)}
            />
            {errMsg && getFieldError('lastHotelNight') && (
              <span className="error-text">
                {getFieldError('lastHotelNight')}
              </span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('arrivalDate')
                ? 'arrivalDate error'
                : 'arrivalDate'
            }
          >
            <label className="required">Arrival date: </label>
            <input
              type="date"
              name="arrivalDate"
              value={perDiemData.arrivalDate || ''}
              required
              onChange={(e) => onHandleChange(e)}
            />
            {errMsg && getFieldError('arrivalDate') && (
              <span className="error-text">{getFieldError('arrivalDate')}</span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('departureDate')
                ? 'departureDate error'
                : 'departureDate'
            }
          >
            <label className="required">Departure date: </label>
            <input
              type="date"
              name="departureDate"
              value={perDiemData.departureDate || ''}
              required
              onChange={(e) => onHandleChange(e)}
            />
            {errMsg && getFieldError('departureDate') && (
              <span className="error-text">
                {getFieldError('departureDate')}
              </span>
            )}
          </div>
        </div>
      </fieldset>
      <fieldset className="mileageDates">
        <legend>Mileage date</legend>
        {perDiemData.mileageDates.map((mileageDate, i) => {
          return (
            <Fragment key={'perDiem_' + i + 1}>
              <label>{`Mileage ${i}`}</label>
              <div id="mileageDate" className="perDiem-mileage-date-container">
                <div
                  className={
                    errMsg && hasArrayFieldError('mileageDates', i, 'date')
                      ? 'mileageDate error'
                      : 'mileageDate'
                  }
                >
                  <label className="required">Date</label>
                  <input
                    type="date"
                    name="date"
                    onChange={(e) => updateMileages(e, i)}
                    value={mileageDate.date || ''}
                  />
                  {errMsg && getArrayFieldError('mileageDates', i, 'date') && (
                    <span className="error-text">
                      {getArrayFieldError('mileageDates', i, 'date')}
                    </span>
                  )}
                </div>
                <div className="perdiem-location1">
                  <Store
                    storeList={stores}
                    header="Start location:"
                    storeValue={perDiemData.mileageDates[i].location1?.id}
                    elementName="location1"
                    index={i}
                    changeStoreInfo={updateMileages}
                  />
                  {errMsg &&
                    getArrayFieldError('mileageDates', i, 'location1.id') && (
                      <span className="error-text">
                        {getArrayFieldError('mileageDates', i, 'location1.id')}
                      </span>
                    )}
                </div>
                <div className="perdiem-location2">
                  <Store
                    storeList={stores}
                    header="End location:"
                    storeValue={perDiemData.mileageDates[i].location2?.id}
                    elementName="location2"
                    index={i}
                    changeStoreInfo={updateMileages}
                  />
                  {errMsg &&
                    getArrayFieldError('mileageDates', i, 'location2.id') && (
                      <span className="error-text">
                        {getArrayFieldError('mileageDates', i, 'location2.id')}
                      </span>
                    )}
                </div>
              </div>
              <div className="rt-ow">
                <label className="required">Select RT/OW:</label>
                <div>
                  Round trip
                  <input
                    type="radio"
                    checked={mileageDate.rt_ow === 'RT'}
                    id="rt"
                    name={'rt_ow_' + i}
                    value="RT"
                    onChange={(e) => updateMileages(e, i)}
                  />
                </div>
                <div>
                  One way
                  <input
                    type="radio"
                    checked={mileageDate.rt_ow === 'OW'}
                    id="ow"
                    name={'rt_ow_' + i}
                    value="OW"
                    onChange={(e) => updateMileages(e, i)}
                  />
                </div>
              </div>
              <div className="perDiem-add-remove-btns">
                <div className="add">
                  <button onClick={addMileageEntry}>Add mileage</button>
                </div>
                {perDiemData.mileageDates.length > 1 && (
                  <div className="remove">
                    <button onClick={(e) => deleteMileageEntry(i)}>
                      {' '}
                      Remove mileage
                    </button>
                  </div>
                )}
              </div>
            </Fragment>
          );
        })}
      </fieldset>
      <fieldset className="reasonMissedWrapper">
        <legend>Comments</legend>
        <Comment
          onHandleChange={handleComments}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>
    </Fragment>
  );
};
// eslint-disable-next-line
PerDiem.propTypes = {};

export default PerDiem;
